/**
 * Task Tracking Widget Component
 * Displays task completion metrics and upcoming tasks
 * Phase 6.3 - AI Insights Section
 */

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  Chip,
  Avatar,
  Skeleton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Assignment as TaskIcon,
  CheckCircle as CompletedIcon,
  Schedule as PendingIcon,
  Warning as OverdueIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';

export interface TaskSummary {
  total: number;
  completed: number;
  pending: number;
  overdue: number;
  completionRate: number;
}

export interface UpcomingTask {
  id: string;
  title: string;
  assignee: string;
  dueDate: string;
  priority: 'high' | 'medium' | 'low';
  status: 'pending' | 'in_progress' | 'completed' | 'overdue';
  leadName?: string;
}

export interface TaskTrackingWidgetProps {
  summary: TaskSummary;
  upcomingTasks: UpcomingTask[];
  loading?: boolean;
}

const TaskTrackingWidget: React.FC<TaskTrackingWidgetProps> = ({
  summary,
  upcomingTasks,
  loading = false
}) => {
  // Get priority color
  const getPriorityColor = (priority: UpcomingTask['priority']) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  // Get status icon and color
  const getStatusDisplay = (status: UpcomingTask['status']) => {
    switch (status) {
      case 'completed':
        return { icon: <CompletedIcon />, color: 'success.main', label: 'Completed' };
      case 'in_progress':
        return { icon: <PendingIcon />, color: 'info.main', label: 'In Progress' };
      case 'overdue':
        return { icon: <OverdueIcon />, color: 'error.main', label: 'Overdue' };
      default:
        return { icon: <PendingIcon />, color: 'text.secondary', label: 'Pending' };
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  // Calculate progress color
  const getProgressColor = (rate: number) => {
    if (rate >= 80) return 'success';
    if (rate >= 50) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TaskIcon color="primary" />
          <Typography variant="h6" fontWeight="600">
            Task Tracking
          </Typography>
        </Box>
        <Skeleton variant="rectangular" height={80} sx={{ mb: 2, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Avatar sx={{ bgcolor: 'secondary.main', width: 36, height: 36 }}>
          <TaskIcon sx={{ fontSize: 20 }} />
        </Avatar>
        <Box>
          <Typography variant="h6" fontWeight="600">
            Task Tracking
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {summary.total} tasks total
          </Typography>
        </Box>
      </Box>

      {/* Summary Stats */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
        {/* Completion Progress */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Completion Rate
            </Typography>
            <Typography variant="body2" fontWeight="600">
              {summary.completionRate.toFixed(1)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={summary.completionRate}
            color={getProgressColor(summary.completionRate)}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        {/* Task Counts */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CompletedIcon sx={{ fontSize: 18, color: 'success.main' }} />
            <Typography variant="body2">
              <strong>{summary.completed}</strong> Completed
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PendingIcon sx={{ fontSize: 18, color: 'info.main' }} />
            <Typography variant="body2">
              <strong>{summary.pending}</strong> Pending
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <OverdueIcon sx={{ fontSize: 18, color: 'error.main' }} />
            <Typography variant="body2">
              <strong>{summary.overdue}</strong> Overdue
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Upcoming Tasks */}
      <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1.5 }}>
        Upcoming Tasks
      </Typography>

      {upcomingTasks.length > 0 ? (
        <List disablePadding>
          {upcomingTasks.map((task, index) => {
            const statusDisplay = getStatusDisplay(task.status);
            return (
              <React.Fragment key={task.id}>
                <ListItem
                  sx={{
                    px: 1.5,
                    py: 1,
                    bgcolor: task.status === 'overdue' ? 'error.50' : 'transparent',
                    borderRadius: 1,
                    mb: 0.5,
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 40 }}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: statusDisplay.color,
                        color: 'white'
                      }}
                    >
                      {statusDisplay.icon}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight="500" noWrap sx={{ flex: 1 }}>
                          {task.title}
                        </Typography>
                        <Chip
                          label={task.priority}
                          size="small"
                          color={getPriorityColor(task.priority)}
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.65rem', textTransform: 'uppercase' }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {task.assignee}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography
                            variant="caption"
                            color={task.status === 'overdue' ? 'error.main' : 'text.secondary'}
                            fontWeight={task.status === 'overdue' ? 600 : 400}
                          >
                            {formatDate(task.dueDate)}
                          </Typography>
                        </Box>
                        {task.leadName && (
                          <Typography variant="caption" color="primary.main">
                            {task.leadName}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {index < upcomingTasks.length - 1 && <Divider component="li" />}
              </React.Fragment>
            );
          })}
        </List>
      ) : (
        <Box
          sx={{
            textAlign: 'center',
            py: 3,
            color: 'text.secondary'
          }}
        >
          <TaskIcon sx={{ fontSize: 36, mb: 1, opacity: 0.5 }} />
          <Typography variant="body2">No upcoming tasks</Typography>
        </Box>
      )}
    </Box>
  );
};

export default TaskTrackingWidget;
